/**
 * Purge des traces locales au changement de membre (US-47, vague E).
 *
 * Les vecteurs 18 et 19 de la note de partage familial n'ont rien à voir avec
 * l'API : ils vivent **sur l'appareil**, et survivent à la déconnexion. Sur la
 * tablette de la cuisine, utilisée par tout le monde, ils suffisent à trahir.
 *
 * - `Cache Storage` du service worker : `/api/recipes*` et les photos y sont
 *   mis en cache, sous un nom fixe qui survit à tout. Une fiche privée
 *   consultée par l'un restait consultable **hors connexion** par le suivant.
 * - `localStorage` et `sessionStorage` : recettes épinglées, portions
 *   choisies, minuteurs **nommés d'après le texte de l'étape**. Un minuteur en
 *   cours nommé « Laisser reposer la pâte à kouign-amann » suffit.
 * - La file d'écritures hors ligne : si un membre se déconnecte et qu'un autre
 *   se connecte avant le rejeu, les écritures du premier seraient attribuées
 *   au second (cas 14). Rare, silencieux, et il corrompt l'attribution.
 *
 * **Cloisonner par membre plutôt que purger serait une fausse bonne idée** :
 * le cache et le stockage local sont lisibles par tout le JavaScript de
 * l'origine, donc par la session suivante. Seul l'effacement protège.
 */

/** Clé du membre dont les traces sont actuellement sur cet appareil. */
const CLE_MEMBRE_COURANT = 'mlc:membre-courant';

/**
 * Clés à conserver : ce sont des préférences d'**appareil**, pas de personne.
 * Le thème et la taille de texte de la tablette de cuisine ne changent pas
 * parce que quelqu'un d'autre se connecte, et les effacer serait une gêne sans
 * aucun gain — ils ne disent rien d'une recette.
 */
const CLES_CONSERVEES = new Set(['mlc:theme', 'mlc:taille-texte', 'mlc:systeme-unites', 'mlc:dernier-membre']);

function effacerStockage(storage: Storage): void {
  try {
    for (const cle of Object.keys(storage)) {
      if (cle.startsWith('mlc:') && !CLES_CONSERVEES.has(cle)) storage.removeItem(cle);
    }
  } catch {
    // Stockage indisponible (navigation privée stricte) : rien à purger.
  }
}

/** Vide le cache du service worker. Sans effet si l'API n'existe pas. */
export async function purgerCaches(): Promise<void> {
  if (typeof caches === 'undefined') return;
  try {
    const noms = await caches.keys();
    await Promise.all(noms.map((nom) => caches.delete(nom)));
  } catch {
    // Le cache n'est pas accessible : rien à purger, et rien à signaler.
  }
}

/** Efface toute trace locale du membre précédent. */
export async function purgerTracesLocales(): Promise<void> {
  effacerStockage(localStorage);
  try {
    effacerStockage(sessionStorage);
  } catch { /* idem */ }
  await purgerCaches();
}

/**
 * Compare le membre connecté à celui dont les traces sont sur cet appareil, et
 * purge si ce n'est pas le même.
 *
 * Appelée au démarrage et après chaque connexion : c'est le seul moment où
 * l'on sait qui est là, et le rejeu de la file hors ligne peut survenir dès la
 * première reconnexion.
 */
export async function adopterAppareil(memberId: string | null): Promise<void> {
  let precedent: string | null = null;
  try {
    precedent = localStorage.getItem(CLE_MEMBRE_COURANT);
  } catch { /* stockage indisponible */ }

  if (precedent === memberId) return;

  await purgerTracesLocales();
  try {
    if (memberId === null) localStorage.removeItem(CLE_MEMBRE_COURANT);
    else localStorage.setItem(CLE_MEMBRE_COURANT, memberId);
  } catch { /* stockage indisponible */ }
}
